import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
} from '@axo/form/configs';
import { CheckboxFieldUI } from '@axo/form/feature/form-input/CheckboxField/CheckboxFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Text } from '@axo/ui-core/components/typography';
import { MouseEvent, ReactElement, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { PopupConsentUI } from '../../../../components/PopupConsent/PopupConsentUI';
import type { Step1Data } from '../FormData.dk.types';
import styles from './Consent.module.scss';

type ConsentSectionProps = {
  showMarketingConsent?: boolean;
  showCreditConsent?: boolean;
  applicant?: 'main' | 'coapp';
};

export function ConsentSection({
  showMarketingConsent = true,
  showCreditConsent = false,
  applicant = 'main',
}: ConsentSectionProps) {
  if (!showCreditConsent && !showMarketingConsent) return null;

  return (
    <Stack gap="2xs" className={styles.consentSection}>
      {showCreditConsent && <CreditConsent applicant={applicant} />}
      {showMarketingConsent && <MarketingConsent applicant={applicant} />}
    </Stack>
  );
}

const CreditConsent = ({
  applicant,
}: {
  applicant: ConsentSectionProps['applicant'];
}): ReactElement => {
  const { t } = useTranslation();

  const creditConsentName =
    applicant === 'coapp'
      ? 'LoanApplication/CoApplicantCreditStatusConsent'
      : 'LoanApplication/CreditStatusConsent';

  return (
    <>
      <Controller
        name={creditConsentName}
        render={({ field: { name, value } }) => (
          <CheckboxFieldUI
            name={name}
            value={value}
            className={styles.creditConsent}
          >
            {t('KreditConsent.label')}
          </CheckboxFieldUI>
        )}
      />
      <div className={styles.textarea}>
        <Text
          as="p"
          size="s"
          dangerouslySetInnerHTML={{ __html: t('KreditConsent.info') }}
        />
      </div>
    </>
  );
};

type PopupContent = {
  title: string;
  elements: string[];
};

const popupContent: Record<string, PopupContent> = {
  services: {
    title: 'services',
    elements: getMarketingConsentServices('dk'),
  },
  partners: {
    title: 'partners',
    elements: getMarketingConsentPartners('dk'),
  },
};

function MarketingConsent({
  applicant = 'main',
}: {
  applicant: ConsentSectionProps['applicant'];
}): ReactElement {
  const { t } = useTranslation();

  const [visiblePopup, setVisiblePopup] = useState<'services' | 'partners'>();

  const hidePopup = () => setVisiblePopup(undefined);
  const openPopup = (e: MouseEvent, type: 'services' | 'partners') => {
    e.preventDefault();
    setVisiblePopup(type);
  };
  const openServicesPopup = (e: MouseEvent) => openPopup(e, 'services');
  const openPartnersPopup = (e: MouseEvent) => openPopup(e, 'partners');

  const marketingConsentName =
    applicant === 'coapp'
      ? 'Ignore/CoApplicantMarketingConsent'
      : 'Ignore/MarketingConsent';

  return (
    <>
      <Controller
        name={marketingConsentName}
        render={({ field: { name, value } }) => (
          <CheckboxFieldUI<Step1Data>
            name={name}
            value={value}
            data-testid="marketing-consent-checkbox"
          >
            <span>
              <Trans
                i18nKey="MarketingConsent.label"
                t={t}
                components={[
                  <span
                    key="services"
                    className={styles.term}
                    onClick={openServicesPopup}
                  />,
                  <span
                    key="partners"
                    className={styles.term}
                    onClick={openPartnersPopup}
                  />,
                  // content is set on the translation
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    key="privacy"
                    className={styles.term}
                    href="https://lendme.dk/kundeservice/vilkaar"
                    target="_blank"
                    rel="noreferrer"
                  />,
                  // content is set on the translation
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    key="privacy_policy"
                    className={styles.term}
                    href="https://lendme.dk/kundeservice/privatlivspolitik"
                    target="_blank"
                    rel="noreferrer"
                  />,
                ]}
              />
            </span>
          </CheckboxFieldUI>
        )}
      />

      {visiblePopup && (
        <PopupConsentUI
          title={t(`MarketingConsent.${popupContent[visiblePopup].title}`)}
          partnersList={popupContent[visiblePopup].elements}
          buttonText={t('MarketingConsent.ok')}
          onClick={hidePopup}
        />
      )}
    </>
  );
}
